/**
 * apierror.ts — turn a failed API response body into a sentence a human can act on.
 *
 * WHY THIS EXISTS. `getJSON` used to build its message as `${status} ${body.detail}`. That is
 * correct for FastAPI's `HTTPException(detail="...")`, where `detail` is a string — and WRONG for a
 * 422, where `detail` is an ARRAY OF OBJECTS. A template literal on an array of objects yields
 * `Object.prototype.toString`, so Andrei ticked the `setup` overlay on 2026-08-03 and the panel
 * told him:
 *
 *     setups: Error: 422 [object Object]
 *
 * The response body actually said which parameter was missing:
 *
 *     {"detail":[{"loc":["query","inversion_rule"],"msg":"Field required","type":"missing"}]}
 *
 * so the information was present the whole way to the browser and thrown away by the last line
 * of code that touched it.
 *
 * WHY A SEPARATE MODULE, and not four lines inside `getJSON`: `getJSON` needs `fetch`, a session and
 * a DOM, so vitest cannot witness it — the same reason `auth.ts` and `aichat.ts` are separate. A
 * pure function over an already-parsed body is testable, and the bug being fixed is precisely a
 * formatting bug in a string nobody had a test for.
 *
 * NOT AN ERROR FRAMEWORK. Two functions: one formats an HTTP failure, one joins several of them
 * into the line a pane shows. Both pure, both called from the one place that needs them.
 */

/** One FastAPI validation error, as `RequestValidationError` serialises it. */
type Validation = { loc?: unknown[]; msg?: string };

/**
 * The message for a non-2xx response. `status` is always shown: it is the one fact that is never
 * ambiguous, and "422" vs "503" is the difference between "the request was wrong" and "the detector
 * is not deployed".
 *
 * DELIBERATE ON JARGON. This string reaches THE FAILING PANE'S OWN `.pane-bar` (via
 * `paneErrorLine` below and `renderPaneError` in main.ts) and the global `#status` line — not the
 * wallet/profile copy that `wallet.test.ts` forbids infrastructure names in. Re-scoped 2026-08-18
 * (card t_677775e0): this comment used to name "the overlay inspector", which was deleted with the
 * right-hand diagnostics panel, and a comment naming a dead sink is how the next reader routes a
 * message somewhere nobody looks. The sink is PER PANE because `#overlay-summary` is one shared box
 * that whichever pane refreshes last overwrites, so a 1D failure was erased by 15m's refresh
 * milliseconds later — the pane that failed is the pane that must say so.
 *
 * A parameter name (`inversion_rule`) is the actionable part of a 422, and blanking it is what
 * produced `[object Object]` in the first place. No module paths, no environment names, no stack:
 * just status, field and reason.
 */
export function apiErrorMessage(status: number, body: unknown, statusText = ''): string {
  const detail = (body as { detail?: unknown } | null)?.detail;

  // 422: `detail` is a list of validation failures. Name the FIELD, because that is what the caller
  // has to change. `loc` is like ["query", "inversion_rule"] — the last element is the parameter.
  if (Array.isArray(detail)) {
    const parts = (detail as Validation[])
      .map((d) => {
        const loc = Array.isArray(d?.loc) ? d.loc : [];
        const field = loc.length ? String(loc[loc.length - 1]) : '';
        const msg = typeof d?.msg === 'string' && d.msg ? d.msg : 'invalid';
        return field ? `${field}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (parts.length) return `${status} ${parts.join('; ')}`;
  }

  // The ordinary case: `HTTPException(detail="no bars for BTC/1D")`.
  if (typeof detail === 'string' && detail) return `${status} ${detail}`;

  // A dict-shaped detail, or anything else non-empty. JSON beats `[object Object]`: it is ugly but
  // it CARRIES the answer, and a lie-free ugly message is the whole contract of this app.
  if (detail !== undefined && detail !== null && typeof detail !== 'string') {
    try {
      const json = JSON.stringify(detail);
      if (json && json !== '{}' && json !== '[]') return `${status} ${json}`;
    } catch {
      /* circular — fall through to statusText */
    }
  }

  return `${status} ${statusText || 'request failed'}`;
}

/**
 * The line ONE pane shows when overlay fetches failed on it.
 *
 * A PURE FUNCTION, not DOM, for the reason above: `refreshOverlaysOnce` needs a chart, a session
 * and a backend, so nothing about it is witnessable — but "what does the user read when two
 * overlays fail at once" is exactly the decision that must be pinned. `renderPaneError` in main.ts
 * writes the result and does nothing else.
 *
 * EMPTY IN MEANS EMPTY OUT, and the caller REMOVES the row rather than painting a blank one: a
 * cleared error must vanish, or the next refresh shows a stale failure for a fetch that has since
 * succeeded. That is the same erase-on-toggle contract every overlay's geometry already has.
 *
 * EVERY failure is named, not just the first. Two overlays failing for different reasons is the
 * normal shape of "the detector is not deployed" (503 on several routes at once), and a line that
 * showed one of them would make the others look fine. `;` joins them because the messages already
 * contain `:`.
 */
export function paneErrorLine(labelled: [string, string][]): string {
  const parts = labelled
    .filter(([label, why]) => label || why)
    .map(([label, why]) => (label ? `${label}: ${why}` : why));
  return parts.length ? `\u26a0 ${parts.join('; ')}` : '';
}
